/**
 * ============================================================================
 * VEDANGJYOTISHAM V6.5 — VARTMAN STHITHI: "WHAT'S ON YOUR MIND?"
 * CONCERN INTELLIGENCE & EPISTEMIC TIMING MASTER TYPE DEFINITIONS
 * 
 * Governing Decisions: GOV-066, docs/V6_4_MASTER_DECISION_REGISTER.md
 * Core Principles:
 * 1. Zero Hallucinated Symptoms: User declared concerns map to Bhavas/Dashas/Transits.
 * 2. Multi-Window Timing: Dissects broad periods without arithmetic midpoint auto-adoption.
 * 3. 6-Tier Epistemic Labeling: Strict labeling across 100% of insights.
 * 4. Safety & Non-Causation: Planets do not cause medical/psychological illness;
 *    upayas are grounding practices, not guaranteed cures.
 * 5. Seeker vs. Acharya Parity: Single calculation core shared identically.
 * ============================================================================
 */

import { CompleteKundali, Language, PlanetName } from './kundali';

// ----------------------------------------------------------------------------
// 1. 7 Canonical Concern Domains
// ----------------------------------------------------------------------------
export type ConcernDomainId =
  | 'INNER_WELLBEING'
  | 'CAREER'
  | 'FINANCES'
  | 'HEALTH_VITALITY'
  | 'RELATIONSHIPS'
  | 'FAMILY_HOME'
  | 'SPIRITUAL_DIRECTION';

export interface ConcernDomainMeta {
  id: ConcernDomainId;
  labelEn: string;
  labelHi: string;
  sanskritName: string;
  descriptionEn: string;
  descriptionHi: string;
  primaryHouses: number[];
  karakas: PlanetName[];
  iconName: string;
}

export const CANONICAL_CONCERN_DOMAINS: Record<ConcernDomainId, ConcernDomainMeta> = {
  INNER_WELLBEING: {
    id: 'INNER_WELLBEING',
    labelEn: 'Inner Wellbeing & Mental Peace',
    labelHi: 'आंतरिक स्वास्थ्य एवं मनोस्थिति',
    sanskritName: 'Manas & Chitta Sthiti (चित्त एवं मानस)',
    descriptionEn: 'Emotional equilibrium, cognitive focus, inner clarity, and peace of mind.',
    descriptionHi: 'मानसिक संतुलन, भावनात्मक स्पष्टता, ध्यान और आंतरिक शांति।',
    primaryHouses: [1, 4, 5],
    karakas: ['Moon', 'Mercury', 'Jupiter'],
    iconName: 'Moon'
  },
  CAREER: {
    id: 'CAREER',
    labelEn: 'Career & Professional Growth',
    labelHi: 'कार्यक्षेत्र एवं आजीविका',
    sanskritName: 'Karma & Vritti (कर्म एवं वृत्ति)',
    descriptionEn: 'Professional role, vocation, enterprise, public recognition, and karmic trajectory.',
    descriptionHi: 'व्यावसायिक प्रगति, कर्म क्षेत्र, पद-प्रतिष्ठा और कर्तव्य दिशा।',
    primaryHouses: [10, 6, 11],
    karakas: ['Sun', 'Saturn', 'Mercury', 'Jupiter'],
    iconName: 'Briefcase'
  },
  FINANCES: {
    id: 'FINANCES',
    labelEn: 'Finances & Wealth Inflow',
    labelHi: 'वित्त, आय एवं संपत्ति',
    sanskritName: 'Dhana & Artha (धन एवं अर्थ)',
    descriptionEn: 'Financial liquidity, accumulated savings, income streams, and resource management.',
    descriptionHi: 'आर्थिक स्थिति, बचत, आय के स्रोत और संपत्ति संरक्षण।',
    primaryHouses: [2, 11, 9],
    karakas: ['Jupiter', 'Venus', 'Mercury'],
    iconName: 'TrendingUp'
  },
  HEALTH_VITALITY: {
    id: 'HEALTH_VITALITY',
    labelEn: 'Vitality & Physical Energy',
    labelHi: 'ऊर्जा, बल एवं शारीरिक सामर्थ्य',
    sanskritName: 'Sharira Bala & Ojas (शरीर बल एवं ओज)',
    descriptionEn: 'Constitutional stamina, vitality rhythm, daily recuperation, and physical balance.',
    descriptionHi: 'शारीरिक ऊर्जा, जीवन शक्ति, दैनिक स्वास्थ्य संतुलन एवं ताजगी।',
    primaryHouses: [1, 6, 8],
    karakas: ['Sun', 'Mars'],
    iconName: 'Activity'
  },
  RELATIONSHIPS: {
    id: 'RELATIONSHIPS',
    labelEn: 'Relationships & Partnership',
    labelHi: 'संबंध, साझेदारी एवं वैवाहिक जीवन',
    sanskritName: 'Sambandha & Vivaha (संबंध एवं विवाह)',
    descriptionEn: 'Interpersonal harmony, marital dynamics, emotional reciprocity, and collaboration.',
    descriptionHi: 'पारस्परिक सामंजस्य, दांपत्य जीवन, संबंध प्रगाढ़ता और सहभागिता।',
    primaryHouses: [7, 5, 2],
    karakas: ['Venus', 'Jupiter'],
    iconName: 'Heart'
  },
  FAMILY_HOME: {
    id: 'FAMILY_HOME',
    labelEn: 'Family & Domestic Life',
    labelHi: 'गृह, परिवार एवं घरेलू वातावरण',
    sanskritName: 'Griha & Kutumba (गृह एवं कुटुम्ब)',
    descriptionEn: 'Domestic stability, ancestral roots, parent-child dynamics, and residence.',
    descriptionHi: 'पारिवारिक सुख, घरेलू शांति, पैतृक संबंध और आवास स्थिरता।',
    primaryHouses: [4, 2, 9],
    karakas: ['Moon', 'Jupiter'],
    iconName: 'Home'
  },
  SPIRITUAL_DIRECTION: {
    id: 'SPIRITUAL_DIRECTION',
    labelEn: 'Spiritual Direction & Purpose',
    labelHi: 'आध्यात्मिक दिशा एवं धर्म मार्ग',
    sanskritName: 'Dharma & Moksha (धर्म एवं मोक्ष)',
    descriptionEn: 'Higher purpose, spiritual practice (sadhana), ethical alignment, and detachment.',
    descriptionHi: 'जीवन का उच्च उद्देश्य, साधना, नैतिक आचरण एवं आत्म-विकास।',
    primaryHouses: [9, 5, 12],
    karakas: ['Jupiter', 'Ketu', 'Sun'],
    iconName: 'Compass'
  }
};

// ----------------------------------------------------------------------------
// 2. Six-Tier Epistemic Evidence Classification
// ----------------------------------------------------------------------------
export type EpistemicCategory =
  | 'FACT_CALCULATED'
  | 'RULE_CLASSICAL'
  | 'INTERPRETATION_PARAM'
  | 'INFERENCE_SYNTHESIS'
  | 'SPECULATION_UNVERIFIED'
  | 'UNCERTAINTY_BOUNDARY';

export interface EpistemicBadgeConfig {
  tag: string;
  badgeLabelEn: string;
  badgeLabelHi: string;
  descriptionEn: string;
  descriptionHi: string;
  colorClass: string;
}

export const EPISTEMIC_CLASSIFICATION_REGISTRY: Record<EpistemicCategory, EpistemicBadgeConfig> = {
  FACT_CALCULATED: {
    tag: '[FACT_CALCULATED]',
    badgeLabelEn: 'Calculated Astronomical Fact',
    badgeLabelHi: 'परिकलित खगोलीय तथ्य',
    descriptionEn: 'Mathematically verified planetary coordinate, sign/house placement, or Vimshottari period balance.',
    descriptionHi: 'गणितीय रूप से प्रमाणित ग्रह स्थिति, राशि/भाव विन्यास अथवा विंशोत्तरी अवधि।',
    colorClass: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800'
  },
  RULE_CLASSICAL: {
    tag: '[RULE_CLASSICAL]',
    badgeLabelEn: 'Classical Shastric Rule',
    badgeLabelHi: 'शास्त्रीय सिद्धांत',
    descriptionEn: 'Codified rule from primary Sanskrit treatises (Brihat Parashara Hora Shastra, Phaladeepika, Jaimini Upadesha Sutras).',
    descriptionHi: 'प्रामाणिक संस्कृत ग्रंथों (बृहत्पाराशर होराशास्त्र, फलदीपिका, जैमिनी सूत्र) का उद्धृत नियम।',
    colorClass: 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800'
  },
  INTERPRETATION_PARAM: {
    tag: '[INTERPRETATION_PARAM]',
    badgeLabelEn: 'School-Dependent Parameter',
    badgeLabelHi: 'पारंपरिक व्याख्या मानक',
    descriptionEn: 'Parametric choice dependent on tradition (e.g. Lahiri Ayanamsha, Bhava Chalita, exact aspect orbs).',
    descriptionHi: 'परंपरा आधारित गणना मानक (यथा लाहिड़ी अयनांश, भाव चलित, दृष्टि अंश सीमा)।',
    colorClass: 'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-300 border-indigo-300 dark:border-indigo-800'
  },
  INFERENCE_SYNTHESIS: {
    tag: '[INFERENCE_SYNTHESIS]',
    badgeLabelEn: 'Synthesized Jyotish Inference',
    badgeLabelHi: 'समन्वित फलित निष्कर्ष',
    descriptionEn: 'Deduction combining multiple concurrent factors (Dasha Lord + Transiting Trigger + Ashtakavarga Bindus).',
    descriptionHi: 'दशा, तात्कालिक गोचर एवं अष्टकवर्ग रेखाओं का समन्वित विश्लेषणात्मक निष्कर्ष।',
    colorClass: 'bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 border-sky-300 dark:border-sky-800'
  },
  SPECULATION_UNVERIFIED: {
    tag: '[SPECULATION_UNVERIFIED]',
    badgeLabelEn: 'Unverified Speculation (Quarantined)',
    badgeLabelHi: 'अपुष्ट अनुमान (पृथक्कृत)',
    descriptionEn: 'Preliminary hypothesis lacking full multi-source classical or computational verification; strictly quarantined.',
    descriptionHi: 'प्रारंभिक परिकल्पना जिसे पूर्ण शास्त्रीय अथवा गणितीय पुष्टि प्राप्त नहीं है; पृथक्कृत।',
    colorClass: 'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border-rose-300 dark:border-rose-800'
  },
  UNCERTAINTY_BOUNDARY: {
    tag: '[UNCERTAINTY_BOUNDARY]',
    badgeLabelEn: 'Uncertainty & Analytical Limit',
    badgeLabelHi: 'अनिश्चितता एवं गणना सीमा',
    descriptionEn: 'Explicit boundary declaration (e.g. birth-time sensitivity, continuous plateau with no discrete peak date).',
    descriptionHi: 'स्पष्ट गणना सीमा (यथा जन्म समय संवेदनशीलता, निरंतर स्थिर अंतराल जिसमें कोई एकाकी शिखर नहीं है)।',
    colorClass: 'bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-800'
  }
};

// ----------------------------------------------------------------------------
// 3. Multi-Window Timing Contracts (No Midpoint Auto-Adoption)
// ----------------------------------------------------------------------------
export type TimingWindowType =
  | 'ACTIVE_DASHA_PD'
  | 'EXACT_TRANSIT_TRIGGER'
  | 'CONSOLIDATION_PLATEAU';

export interface ConcernTimingWindow {
  windowId: string;
  windowType: TimingWindowType;
  titleEn: string;
  titleHi: string;
  startDate: string; // e.g. "15 Nov 2026"
  endDate: string;   // e.g. "28 Jan 2027"
  /**
   * Explicitly null when evidence is a continuous plateau or broad window without a discrete astronomical transit orb.
   * Arithmetic midpoints ((start + end) / 2) are STRICTLY BARRED from being emitted as peak dates.
   */
  peakDate: string | null;
  peakDateRationaleEn?: string;
  peakDateRationaleHi?: string;
  durationLabelEn: string;
  durationLabelHi: string;
  status: 'ACTIVE_NOW' | 'UPCOMING' | 'CONSOLIDATING';
  epistemicCategory: EpistemicCategory;
  determiningAstrologicalFactorEn: string;
  determiningAstrologicalFactorHi: string;
  significanceEn: string;
  significanceHi: string;
  /**
   * Sequence index when multiple distinct event windows occur within a single broad theme (0-indexed).
   */
  eventSequenceIndex?: number;
  /**
   * Indicates if this window represents a repeated, oscillating, or reversed event phase
   * (e.g. initial relocation outward vs return relocation, retrograde station vs direct pass).
   */
  isReversedOrRepeated?: boolean;
  eventPhaseDescriptorEn?: string;
  eventPhaseDescriptorHi?: string;
  deduplicationKey?: string;
}

// ----------------------------------------------------------------------------
// 4. Epistemic Insight Statements
// ----------------------------------------------------------------------------
export interface EpistemicInsightStatement {
  id: string;
  category: EpistemicCategory;
  labelTag: string; // e.g. "[FACT_CALCULATED]"
  titleEn: string;
  titleHi: string;
  narrativeEn: string;
  narrativeHi: string;
  shastraReference?: string;
}

// ----------------------------------------------------------------------------
// 5. Psychological Safety & Non-Causation Model
// ----------------------------------------------------------------------------
export interface PsychologicalSafetyFraming {
  medicalDisclaimerEn: string;
  medicalDisclaimerHi: string;
  nonCausationStatementEn: string;
  nonCausationStatementHi: string;
  upayaFramingEn: string;
  upayaFramingHi: string;
}

export interface ConcernUpayaPractice {
  id: string;
  titleEn: string;
  titleHi: string;
  category: 'MINDFULNESS_GROUNDING' | 'CLASSICAL_DHYANA' | 'CHARITABLE_DAAN' | 'LIFESTYLE_RHYTHM';
  practiceEn: string;
  practiceHi: string;
  beneficialPeriodEn: string;
  beneficialPeriodHi: string;
  nonGuaranteeNoticeEn: string;
  nonGuaranteeNoticeHi: string;
}

// ----------------------------------------------------------------------------
// 6. Free-Text Semantic Evaluation Contract
// ----------------------------------------------------------------------------
export interface FreeTextEvaluationResult {
  rawQuery: string;
  sanitizedQuery: string;
  confidence: 'HIGH' | 'MODERATE' | 'LOW' | 'UNSUPPORTED';
  matchedDomain?: ConcernDomainId;
  matchedKeywords: string[];
  fallbackChipApplied: boolean;
  clarificationPromptEn?: string;
  clarificationPromptHi?: string;
}

// ----------------------------------------------------------------------------
// 7. Acharya Deep-Dive Telemetry
// ----------------------------------------------------------------------------
export interface AcharyaConcernDeepDive {
  natalBhavasEvaluated: Array<{
    house: number;
    sign: string;
    lord: PlanetName;
    occupants: PlanetName[];
    drishtiLords: PlanetName[];
    ashtakavargaBindus: number;
  }>;
  dashaState: {
    mahaLord: PlanetName;
    mahaDignity: string;
    antarLord: PlanetName;
    antarDignity: string;
    pratyantarLord: PlanetName;
    pratyantarDignity: string;
    mutualRelationship: string;
  };
  gocharInfluences: Array<{
    planet: PlanetName;
    currentSign: string;
    currentHouse: number;
    exactDegree: number;
    transitAspectsToNatal: string[];
    orbDegrees: number;
  }>;
  classicalSutrasCited: string[];
}

// ----------------------------------------------------------------------------
// 8. Consolidated Concern Intelligence Result
// ----------------------------------------------------------------------------
export interface VartmanConcernIntelligenceResult {
  declaredDomain: ConcernDomainId;
  freeTextEvaluation?: FreeTextEvaluationResult;
  focusDateFormatted: string;
  focusDateIso: string;
  mappedHouses: number[];
  mappedKarakas: PlanetName[];
  activeDasha: {
    maha: PlanetName;
    antar: PlanetName;
    pratyantar: PlanetName;
  };
  timingWindows: ConcernTimingWindow[];
  epistemicInsights: EpistemicInsightStatement[];
  safetyFraming: PsychologicalSafetyFraming;
  upayas: ConcernUpayaPractice[];
  acharyaTelemetry: AcharyaConcernDeepDive;
}
